// Staff settings — team & access, emails, ordering rules (incl. approvals), billing, data.
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ClientHome from './ClientHome.jsx';
import PageHeader from '../components/PageHeader.jsx';
import { fetchCompanies, fetchTeam, provisionStaff, provisionRep, setUserActive, setUserAccess, resendInvite } from '../api/companies.js';
import { fetchApprovalConfig, saveApprovalConfig, fetchInvoiceEmail, saveInvoiceEmail, fetchClientInvite, saveClientInvite, fetchMinOrder, saveMinOrder, fetchLastSync, runCatalogSync, fetchCatalogSnapshots, fetchCustomerMessages, saveCustomerMessages, fetchRemittance, saveRemittance, fetchClientHomeLayout, saveClientHomeLayout } from '../api/settings.js';
import { fetchTargets, saveTargets, fetchFocus, saveFocus, searchFocusProducts } from '../api/staffDashboard.js';
import { useAuthStore } from '../stores/authStore.js';
import { can, CAP } from '../lib/access.js';
import { fmtMoney } from '../lib/matrix.js';

const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong.';
const field = 'mt-1 w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30';

// Register logins (staff + reps) and revoke/restore access. Everyone signs in with a one-time code;
// only people listed here (active) can log in. Clients are managed from their company page.
const TIER_LABEL = { ADMIN: 'Admin', MANAGER: 'Manager', VIEWER: 'Viewer' };

function TeamSection() {
  const [team, setTeam] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', role: 'INTERNAL_STAFF', accessLevel: 'MANAGER', storeAccess: 'BOTH' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [invite, setInvite] = useState(null); // { email, url } — the last invite link to copy/share
  const [showArchived, setShowArchived] = useState(false);
  const user = useAuthStore((s) => s.user);
  const isAdmin = can(user, CAP.MANAGE_TEAM); // archived list is admin-only

  const load = () => fetchTeam().then(setTeam).catch(() => setTeam([]));
  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const body = { email: form.email.trim(), name: form.name.trim() || undefined };
      let res;
      if (form.role === 'REP') {
        res = await provisionRep({ ...body, storeAccess: form.storeAccess });
      } else {
        res = await provisionStaff({ ...body, accessLevel: form.accessLevel, storeAccess: form.storeAccess });
      }
      setInvite({ email: body.email, url: res.inviteUrl, emailSent: res.emailSent });
      setForm({ email: '', name: '', role: form.role, accessLevel: form.accessLevel, storeAccess: form.storeAccess });
      await load();
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (u) => {
    await setUserActive(u.id, !u.isActive).catch((e) => setErr(errMsg(e)));
    load();
  };

  const changeStore = async (u, storeAccess) => {
    setErr('');
    try {
      await setUserAccess(u.id, { storeAccess });
      await load();
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const doResend = async (u) => {
    setErr('');
    try {
      const r = await resendInvite(u.id);
      setInvite({ email: u.email, url: r.inviteUrl, emailSent: r.emailSent });
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  return (
    <div className="mt-6 max-w-lg rounded-lg border border-line bg-surface p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-text-primary">Team &amp; access</h2>
      <p className="mt-0.5 text-xs text-text-muted">
        Everyone signs in with a one-time email code — but only registered, active people can log in. Add staff and reps
        here; clients are added from their company page.
      </p>

      <form onSubmit={add} className="mt-3 flex flex-wrap items-end gap-2">
        <select
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          className="rounded-md border border-line bg-surface px-2 py-2 text-sm"
        >
          <option value="INTERNAL_STAFF">Staff</option>
          <option value="REP">Rep</option>
        </select>
        {form.role === 'INTERNAL_STAFF' && (
          <select
            value={form.accessLevel}
            onChange={(e) => setForm({ ...form, accessLevel: e.target.value })}
            title="Capability level"
            className="rounded-md border border-line bg-surface px-2 py-2 text-sm"
          >
            <option value="ADMIN">Admin</option>
            <option value="MANAGER">Manager</option>
            <option value="VIEWER">Viewer</option>
          </select>
        )}
        {/* Store access applies to both staff and reps. */}
        <select
          value={form.storeAccess}
          onChange={(e) => setForm({ ...form, storeAccess: e.target.value })}
          title="Store access"
          className="rounded-md border border-line bg-surface px-2 py-2 text-sm"
        >
          <option value="BOTH">All stores</option>
          <option value="A">Australia (A)</option>
          <option value="B">International (B)</option>
        </select>
        <input
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="email@anywhere.com"
          className="w-56 rounded-md border border-line px-2.5 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
        />
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Name (optional)"
          className="w-40 rounded-md border border-line px-2.5 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
        />
        <button
          type="submit"
          disabled={busy || !form.email.trim()}
          className="rounded-md bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
        >
          {busy ? '…' : 'Register'}
        </button>
      </form>
      {err && <p className="mt-2 text-xs text-error">{err}</p>}

      {invite && (
        <div className="mt-3 rounded-md border border-accent/40 bg-accent-light/50 p-2.5 text-xs">
          <div className="mb-1 font-medium text-text-primary">
            {invite.emailSent
              ? `✓ Login instructions emailed to ${invite.email}. Link (to copy if needed):`
              : `⚠ Couldn't email ${invite.email} — copy this link and share it:`}
          </div>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={invite.url}
              onFocus={(e) => e.target.select()}
              className="min-w-0 flex-1 rounded border border-line bg-surface px-2 py-1 font-mono text-[11px]"
            />
            <button
              type="button"
              onClick={() => navigator.clipboard?.writeText(invite.url)}
              className="shrink-0 rounded border border-line px-2 py-1 font-medium text-text-secondary hover:bg-surface"
            >
              Copy
            </button>
          </div>
          <p className="mt-1 text-text-muted">They set a password, then sign in with email + password + a one-time code.</p>
        </div>
      )}

      <div className="mt-4 space-y-1.5">
        {(team || []).filter((u) => u.isActive).map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-2 border-b border-line py-1.5 text-sm last:border-0">
            <div className="min-w-0">
              <Link to={`/team/${u.id}`} className="font-medium text-text-primary hover:text-accent hover:underline">
                {u.name || u.email}
              </Link>
              <span className="ml-2 rounded bg-surface-raised px-1.5 py-0.5 text-[10px] text-text-muted">
                {u.role === 'REP' ? 'Rep' : `Staff · ${TIER_LABEL[u.accessLevel] || u.accessLevel}`}
              </span>
              {(u.role === 'INTERNAL_STAFF' || u.role === 'REP') && (
                <select
                  value={u.storeAccess || 'BOTH'}
                  onChange={(e) => changeStore(u, e.target.value)}
                  title="Store access"
                  className="ml-1 rounded border border-line bg-surface px-1 py-0.5 text-[10px] text-text-secondary"
                >
                  <option value="BOTH">All stores</option>
                  <option value="A">Australia</option>
                  <option value="B">International</option>
                </select>
              )}
              {!u.registered && <span className="ml-1 rounded bg-warning-light px-1.5 py-0.5 text-[10px] text-warning">invited</span>}
              <div className="truncate text-xs text-text-muted">{u.email}</div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                onClick={() => doResend(u)}
                className="rounded-md border border-line px-2 py-1 text-xs font-medium text-text-secondary hover:bg-surface-raised"
              >
                {u.registered ? 'Re-invite' : 'Resend'}
              </button>
              <button
                onClick={() => toggle(u)}
                className="rounded-md border border-line px-2.5 py-1 text-xs font-medium text-error hover:bg-error-light"
                title="Revoke access — moves them to the archived list"
              >
                Revoke
              </button>
            </div>
          </div>
        ))}
        {team && (team || []).filter((u) => u.isActive).length === 0 && (
          <p className="text-xs text-text-muted">No active team members.</p>
        )}
      </div>

      {/* Archived / revoked team members — admin-only. Revoking moves someone here; Restore brings
          them back. Their record + history is preserved; they simply can't log in while archived. */}
      {isAdmin && (team || []).some((u) => !u.isActive) && (
        <div className="mt-4 border-t border-line pt-3">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="flex w-full items-center justify-between text-xs font-semibold text-text-secondary hover:text-text-primary"
          >
            <span>Archived team members · {(team || []).filter((u) => !u.isActive).length}</span>
            <span className="font-medium text-accent">{showArchived ? 'Hide' : 'Show'}</span>
          </button>
          {showArchived && (
            <div className="mt-2 space-y-1.5">
              {(team || []).filter((u) => !u.isActive).map((u) => (
                <div key={u.id} className="flex items-center justify-between gap-2 rounded-md bg-surface-raised/40 px-2.5 py-1.5 text-sm">
                  <div className="min-w-0">
                    <Link to={`/team/${u.id}`} className="font-medium text-text-secondary hover:text-accent hover:underline">
                      {u.name || u.email}
                    </Link>
                    <span className="ml-2 rounded bg-surface-raised px-1.5 py-0.5 text-[10px] text-text-muted">
                      {u.role === 'REP' ? 'Rep' : `Staff · ${TIER_LABEL[u.accessLevel] || u.accessLevel}`}
                    </span>
                    <div className="truncate text-xs text-text-muted">{u.email}</div>
                  </div>
                  <button
                    onClick={() => toggle(u)}
                    className="shrink-0 rounded-md border border-line px-2.5 py-1 text-xs font-medium text-success hover:bg-success-light"
                  >
                    Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Reusable subject+body email-template editor (used for the invoice email and the client invite).
// `placeholders` are the tokens the message supports; `sample` fills the live preview.
function EmailTemplateSection({ title, description, placeholders, sample, fetchTpl, saveTpl }) {
  const [tpl, setTpl] = useState(null);
  const [form, setForm] = useState({ subject: '', body: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTpl()
      .then((t) => {
        setTpl(t);
        setForm({ subject: t.subject, body: t.body });
      })
      .catch((e) => setError(errMsg(e)));
  }, [fetchTpl]);

  const fillSample = (s) => String(s || '').replace(/\{(\w+)\}/g, (_, k) => (k in sample ? sample[k] : `{${k}}`));
  const dirty = tpl && (form.subject !== tpl.subject || form.body !== tpl.body);
  const isDefault = tpl && form.subject === tpl.default?.subject && form.body === tpl.default?.body;

  const save = async () => {
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const saved = await saveTpl({ subject: form.subject.trim(), body: form.body.trim() });
      setTpl((t) => ({ ...t, subject: saved.subject, body: saved.body }));
      setMsg('Saved.');
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 max-w-2xl rounded-lg border border-line bg-surface p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
      <p className="mt-1 text-xs text-text-muted">{description}</p>

      {!tpl ? (
        <p className="mt-3 text-sm text-text-muted">Loading…</p>
      ) : (
        <>
          <label className="mt-3 block">
            <span className="text-xs font-medium text-text-muted">Subject</span>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={field} />
          </label>
          <label className="mt-3 block">
            <span className="text-xs font-medium text-text-muted">Message</span>
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={9} className={field} />
          </label>

          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-text-muted">
            <span>Placeholders:</span>
            {placeholders.map((p) => (
              <code key={p} className="rounded bg-surface-raised px-1.5 py-0.5 font-mono text-text-secondary">{p}</code>
            ))}
          </div>

          {/* Live preview with sample values */}
          <div className="mt-3 rounded-md border border-line bg-surface-raised/40 p-3">
            <div className="text-[11px] uppercase tracking-wide text-text-muted">Preview</div>
            <div className="mt-1 text-sm font-semibold text-text-primary">{fillSample(form.subject) || '—'}</div>
            <div className="mt-1 whitespace-pre-wrap text-sm text-text-secondary">{fillSample(form.body) || '—'}</div>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={save}
              disabled={busy || !dirty || !form.subject.trim() || !form.body.trim()}
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save template'}
            </button>
            <button
              onClick={() => { setForm({ subject: tpl.default.subject, body: tpl.default.body }); setMsg(''); setError(''); }}
              disabled={isDefault}
              className="text-sm font-medium text-text-secondary hover:text-text-primary disabled:opacity-40"
            >
              Reset to default
            </button>
            {msg && <span className="text-sm text-success">{msg}</span>}
            {error && <span className="text-sm text-error">{error}</span>}
          </div>
        </>
      )}
    </div>
  );
}

function InvoiceEmailSection() {
  return (
    <EmailTemplateSection
      title="Invoice email template"
      description="The default message for the “Send invoice” button on an order. Staff can still adjust it per send. Use the placeholders below — they’re filled in automatically when the email goes out."
      placeholders={['{contactName}', '{company}', '{invoiceNumber}', '{orderRef}', '{total}']}
      sample={{ contactName: 'Jordan', company: 'Acme Retail', invoiceNumber: 'INV-2026-00042', orderRef: 'T8AU0007', total: 'AUD 1,240.00' }}
      fetchTpl={fetchInvoiceEmail}
      saveTpl={saveInvoiceEmail}
    />
  );
}

function ClientInviteSection() {
  return (
    <EmailTemplateSection
      title="Client invite email"
      description="The message clients receive when you invite them to set up their login. The “Set your password” button and expiry note are always added automatically — this is the intro copy."
      placeholders={['{name}', '{company}', '{inviterName}', '{inviteUrl}']}
      sample={{ name: 'Jordan', company: 'Acme Retail', inviterName: 'Felipe', inviteUrl: 'https://wholesale.teamm8.com/register?token=…' }}
      fetchTpl={fetchClientInvite}
      saveTpl={saveClientInvite}
    />
  );
}

// Minimum order value per store, enforced in the New Order flow (0 = no minimum).
// Automatic customer lifecycle emails, sent as the account's CS owner (admin/managers manage the copy).
function CustomerMessagesSection() {
  const user = useAuthStore((s) => s.user);
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(null);
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCustomerMessages()
      .then((c) => {
        setCfg(c);
        setForm({ discountAnnounce: { ...c.discountAnnounce }, deliveryFollowup: { ...c.deliveryFollowup }, defaultSenderUserId: c.defaultSenderUserId ?? null });
      })
      .catch((e) => setError(errMsg(e)));
    fetchTeam()
      .then((t) => setStaff((t || []).filter((u) => u.role === 'INTERNAL_STAFF' && u.isActive)))
      .catch(() => setStaff([]));
  }, []);

  if (!can(user, CAP.MANAGE_CLIENTS)) return null;

  const setField = (group, key, value) => setForm((f) => ({ ...f, [group]: { ...f[group], [key]: value } }));

  const save = async () => {
    setBusy(true); setMsg(''); setError('');
    try {
      const saved = await saveCustomerMessages({
        defaultSenderUserId: form.defaultSenderUserId ?? null,
        discountAnnounce: { enabled: !!form.discountAnnounce.enabled, subject: form.discountAnnounce.subject.trim(), body: form.discountAnnounce.body.trim() },
        deliveryFollowup: { enabled: !!form.deliveryFollowup.enabled, subject: form.deliveryFollowup.subject.trim(), body: form.deliveryFollowup.body.trim(), delayDays: Number(form.deliveryFollowup.delayDays) || 0 },
      });
      setCfg(saved);
      setMsg('Saved.');
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const editor = (group, { title, hint, placeholders, extra }) => (
    <div className="mt-4 rounded-md border border-line bg-surface-raised/30 p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-text-primary">{title}</h3>
        <label className="flex items-center gap-2 text-xs text-text-secondary">
          <input type="checkbox" checked={!!form[group].enabled} onChange={(e) => setField(group, 'enabled', e.target.checked)} />
          {form[group].enabled ? 'On' : 'Off'}
        </label>
      </div>
      <p className="mt-0.5 text-xs text-text-muted">{hint}</p>
      {extra}
      <label className="mt-3 block">
        <span className="text-xs font-medium text-text-muted">Subject</span>
        <input value={form[group].subject} onChange={(e) => setField(group, 'subject', e.target.value)} className={field} />
      </label>
      <label className="mt-3 block">
        <span className="text-xs font-medium text-text-muted">Message</span>
        <textarea value={form[group].body} onChange={(e) => setField(group, 'body', e.target.value)} rows={8} className={field} />
      </label>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-text-muted">
        <span>Placeholders:</span>
        {placeholders.map((p) => (
          <code key={p} className="rounded bg-surface-raised px-1.5 py-0.5 font-mono text-text-secondary">{p}</code>
        ))}
      </div>
    </div>
  );

  return (
    <div className="mt-4 max-w-2xl rounded-lg border border-line bg-surface p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-text-primary">Customer messages</h2>
      <p className="mt-1 text-xs text-text-muted">
        Automatic, personal emails. They come from the <strong>default sender below for every client</strong> — no per-account setup needed. (You can still set a specific CS owner on an individual client's page to override the sender just for them.)
      </p>

      {!form ? (
        <p className="mt-3 text-sm text-text-muted">Loading…</p>
      ) : (
        <>
          <label className="mt-3 block max-w-sm">
            <span className="text-xs font-medium text-text-muted">Sent by (default, all clients)</span>
            <select
              value={form.defaultSenderUserId || ''}
              onChange={(e) => setForm((f) => ({ ...f, defaultSenderUserId: e.target.value || null }))}
              className={field}
            >
              <option value="">TEAMM8 Customer Care — shared team (replies to the wholesale inbox)</option>
              {staff.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email} — replies to {u.email}</option>
              ))}
            </select>
            <span className="mt-1 block text-[11px] text-text-muted">Since your whole CS team covers all clients, pick your shared CS account (or a CS lead) so replies reach the right inbox.</span>
          </label>
          <p className="mt-4 rounded-md bg-surface-raised/60 px-3 py-2 text-xs text-text-muted">
            Promotion announcements are no longer sent automatically — when you create a promotion you’re offered an announcement email in Marketing, which you review before it goes out.
          </p>
          {editor('deliveryFollowup', {
            title: 'After an order is delivered',
            hint: 'A friendly check-in once the parcel has arrived.',
            placeholders: ['{contactName}', '{company}', '{orderRef}', '{senderName}'],
            extra: (
              <label className="mt-3 flex items-center gap-2 text-xs text-text-secondary">
                Send
                <input
                  type="number" min={0} max={60}
                  value={form.deliveryFollowup.delayDays}
                  onChange={(e) => setField('deliveryFollowup', 'delayDays', e.target.value)}
                  className="w-16 rounded border border-line px-2 py-1 text-xs"
                />
                days after delivery
              </label>
            ),
          })}

          <div className="mt-4 flex items-center gap-3">
            <button onClick={save} disabled={busy} className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50">
              {busy ? 'Saving…' : 'Save'}
            </button>
            {msg && <span className="text-sm text-success">{msg}</span>}
            {error && <span className="text-sm text-error">{error}</span>}
          </div>
        </>
      )}
    </div>
  );
}

function MinOrderSection() {
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMinOrder().then((v) => { setForm(v); setSaved(v); }).catch((e) => setError(errMsg(e)));
  }, []);

  const dirty = form && saved && (Number(form.A) !== Number(saved.A) || Number(form.B) !== Number(saved.B));
  const save = async () => {
    setBusy(true); setMsg(''); setError('');
    try {
      const body = { A: Number(form.A) || 0, B: Number(form.B) || 0 };
      const v = await saveMinOrder(body);
      setSaved(v); setForm(v); setMsg('Saved.');
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 max-w-lg rounded-lg border border-line bg-surface p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-text-primary">Minimum order value</h2>
      <p className="mt-0.5 text-xs text-text-muted">Orders below this can&rsquo;t be submitted in the New Order flow. Set 0 for no minimum. Amounts are in each store&rsquo;s currency.</p>
      {!form ? (
        <p className="mt-3 text-sm text-text-muted">Loading…</p>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-text-muted">TEAMM8 Australia (AUD)</span>
              <input type="number" min={0} step="1" value={form.A} onChange={(e) => setForm({ ...form, A: e.target.value })} className={field} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-text-muted">TEAMM8 International (USD)</span>
              <input type="number" min={0} step="1" value={form.B} onChange={(e) => setForm({ ...form, B: e.target.value })} className={field} />
            </label>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button onClick={save} disabled={busy || !dirty} className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50">
              {busy ? 'Saving…' : 'Save'}
            </button>
            {msg && <span className="text-sm text-success">{msg}</span>}
            {error && <span className="text-sm text-error">{error}</span>}
          </div>
        </>
      )}
    </div>
  );
}

// Approvals: auto-approve trusted clients' orders under a value ceiling (everything else is reviewed).
function ApprovalSection() {
  const user = useAuthStore((s) => s.user);
  const canEdit = can(user, CAP.MANAGE_SETTINGS);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    fetchApprovalConfig().then(setForm).catch((e) => setError(errMsg(e)));
  }, []);
  const save = async () => {
    setBusy(true); setMsg(''); setError('');
    try {
      setForm(await saveApprovalConfig({ valueCeiling: Number(form.valueCeiling) || 0, escalateNewClients: Boolean(form.escalateNewClients), autoApproveTrusted: Boolean(form.autoApproveTrusted) }));
      setMsg('Saved.');
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-lg rounded-lg border border-line bg-surface p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-text-primary">Approvals</h2>
      <p className="mt-0.5 text-xs text-text-muted">
        Every order is reviewed by the team, except — when switched on — orders from clients marked <strong>Trusted</strong> that are under the ceiling, not from a new client and not on credit hold. Those are approved automatically once shipping is confirmed (free lanes confirm instantly), and the invoice goes out straight away.
      </p>
      {!form ? (
        <p className="mt-3 text-sm text-text-muted">{error || 'Loading…'}</p>
      ) : (
        <>
          <label className="mt-3 flex items-center gap-2 text-sm text-text-primary">
            <input type="checkbox" disabled={!canEdit} checked={Boolean(form.autoApproveTrusted)} onChange={(e) => setForm({ ...form, autoApproveTrusted: e.target.checked })} className="accent-accent" />
            Auto-approve trusted clients under the ceiling
          </label>
          <label className="mt-3 block">
            <span className="text-xs font-medium text-text-muted">Approval ceiling (USD, converted for other currencies)</span>
            <input type="number" min={0} step="100" disabled={!canEdit} value={form.valueCeiling} onChange={(e) => setForm({ ...form, valueCeiling: e.target.value })} className={field} />
          </label>
          <label className="mt-3 flex items-center gap-2 text-sm text-text-primary">
            <input type="checkbox" disabled={!canEdit} checked={Boolean(form.escalateNewClients)} onChange={(e) => setForm({ ...form, escalateNewClients: e.target.checked })} className="accent-accent" />
            Always review a client’s first order
          </label>
          {canEdit && (
            <div className="mt-3 flex items-center gap-3">
              <button onClick={save} disabled={busy} className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50">
                {busy ? 'Saving…' : 'Save'}
              </button>
              {msg && <span className="text-sm text-success">{msg}</span>}
              {error && <span className="text-sm text-error">{error}</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Catalog sync — refresh companies/locations from both Shopify stores + clear product caches so new
// products show immediately. Also runs automatically once a day. Admin-only.
function SyncSection() {
  const user = useAuthStore((s) => s.user);
  const [last, setLast] = useState(null);
  const [snapshots, setSnapshots] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLastSync().then(setLast).catch(() => {});
    fetchCatalogSnapshots().then(setSnapshots).catch(() => {});
  }, []);

  if (!can(user, CAP.MANAGE_SETTINGS)) return null;

  const run = async () => {
    setBusy(true); setError('');
    try {
      setLast(await runCatalogSync());
      // Snapshot rebuild runs in the background; re-check the count shortly after.
      setTimeout(() => fetchCatalogSnapshots().then(setSnapshots).catch(() => {}), 4000);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const storeName = { A: 'TEAMM8 Australia', B: 'TEAMM8 International' };
  const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="mt-4 max-w-lg rounded-lg border border-line bg-surface p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-text-primary">Sync products &amp; clients</h2>
      <p className="mt-0.5 text-xs text-text-muted">Pull the latest companies, locations and products from both Shopify stores. Product catalogues are stored so New Order opens instantly; syncing rebuilds them in the background (New Order keeps working meanwhile). Runs automatically once a day.</p>
      <div className="mt-3 flex items-center gap-3">
        <button onClick={run} disabled={busy} className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50">
          {busy ? 'Syncing…' : 'Sync now'}
        </button>
        {error && <span className="text-sm text-error">{error}</span>}
      </div>
      {snapshots && (
        <div className="mt-3 rounded-md border border-line bg-surface-raised/40 p-3 text-xs text-text-muted">
          <div className="font-medium text-text-secondary">Catalogues stored: {snapshots.count}</div>
          {snapshots.newestRefreshedAt && (
            <div className="mt-0.5">
              Refreshed {fmtWhen(snapshots.oldestRefreshedAt)}
              {snapshots.oldestRefreshedAt !== snapshots.newestRefreshedAt ? ` – ${fmtWhen(snapshots.newestRefreshedAt)}` : ''}
            </div>
          )}
        </div>
      )}
      {last && (
        <div className="mt-2 rounded-md border border-line bg-surface-raised/40 p-3 text-xs text-text-muted">
          <div>Last synced {fmtWhen(last.ranAt)} · {last.trigger === 'manual' ? 'manual' : 'automatic'}</div>
          <ul className="mt-1 space-y-0.5">
            {last.stores?.map((s) => (
              <li key={s.store}>
                {storeName[s.store] || s.store}: {s.error ? <span className="text-error">failed — {s.error}</span> : `${s.companies} companies, ${s.locations} locations`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const REMIT_FIELD_LABELS = { beneficiary: 'Beneficiary name', bank: 'Bank', accountNumber: 'Account number', routingNumber: 'Routing / ABA', bsb: 'BSB', sortCode: 'Sort code', iban: 'IBAN', swift: 'SWIFT / BIC', note: 'Note (optional)' };

// Per-currency bank accounts clients remit to. Shown on the "Email the rep" batch notice. Admin-only.
function RemittanceSection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  useEffect(() => { fetchRemittance().then(setData).catch((e) => setError(errMsg(e))); }, []);
  if (!data) return <p className="mt-4 text-sm text-text-muted">{error || 'Loading…'}</p>;
  const currencies = Object.keys(data).filter((k) => k !== 'default');
  const setField = (cur, key, val) => setData((d) => ({ ...d, [cur]: { ...d[cur], [key]: val } }));
  const save = async () => {
    setBusy(true); setMsg(''); setError('');
    try {
      await saveRemittance(Object.fromEntries(currencies.map((c) => [c, data[c]])));
      setMsg('Saved.');
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 max-w-2xl rounded-lg border border-line bg-surface p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-text-primary">Remittance bank accounts</h2>
      <p className="mt-0.5 text-xs text-text-muted">The accounts clients pay into, per currency — shown on the &ldquo;Email the rep&rdquo; batch notice. Leave a currency blank if you don&rsquo;t use it.</p>
      <div className="mt-4 space-y-4">
        {currencies.map((cur) => (
          <div key={cur} className="rounded-md border border-line p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">{cur}</div>
            <div className="grid grid-cols-2 gap-3">
              {Object.keys(data.default?.[cur] || data[cur] || {}).map((key) => (
                <label key={key} className={key === 'note' || key === 'beneficiary' || key === 'bank' ? 'col-span-2 block' : 'block'}>
                  <span className="text-xs font-medium text-text-muted">{REMIT_FIELD_LABELS[key] || key}</span>
                  <input value={data[cur]?.[key] ?? ''} onChange={(e) => setField(cur, key, e.target.value)} className={field} />
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button onClick={save} disabled={busy} className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
        {msg && <span className="text-sm text-success">{msg}</span>}
        {error && <span className="text-sm text-error">{error}</span>}
      </div>
    </div>
  );
}

// The client home page: which sections show, in what order — with a live preview as any client.
function ClientHomeSection() {
  const user = useAuthStore((s) => s.user);
  const editable = can(user, CAP.MANAGE_MARKETING);
  const [available, setAvailable] = useState([]);
  const [sections, setSections] = useState(null);
  const [saved, setSaved] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [previewId, setPreviewId] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    fetchClientHomeLayout()
      .then((d) => {
        setAvailable(d.available);
        setSections(d.sections);
        setSaved(JSON.stringify(d.sections));
      })
      .catch((e) => setError(e.response?.data?.error?.message || 'Couldn’t load the layout.'));
    fetchCompanies()
      .then((l) => {
        const active = (l || []).filter((c) => c.isActive).sort((a, b) => a.name.localeCompare(b.name));
        setCompanies(active);
        // Default to a client who can actually log in, so the preview is a real one.
        setPreviewId((active.find((c) => (c.locations || []).some((l) => l.contacts?.length)) || active[0])?.id || '');
      })
      .catch(() => setCompanies([]));
  }, []);
  if (!sections) return <div className="text-sm text-text-muted">{error || 'Loading…'}</div>;
  const meta = (k) => available.find((a) => a.key === k) || { label: k, hint: '' };
  const move = (i, d) => {
    const next = [...sections];
    const [x] = next.splice(i, 1);
    next.splice(i + d, 0, x);
    setSections(next);
  };
  const dirty = JSON.stringify(sections) !== saved;
  const save = async () => {
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const d = await saveClientHomeLayout(sections);
      setSections(d.sections);
      setSaved(JSON.stringify(d.sections));
      setMsg('Saved — every client sees this layout now.');
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Couldn’t save.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
        <h2 className="text-base font-semibold text-text-primary">Client home page</h2>
        <p className="mt-1 text-sm text-text-muted">
          Choose what every client sees when they log in, and in what order. “What you owe” and “Orders &amp; invoices” always show.
          {!editable && ' You can view this, but only admins and managers can change it.'}
        </p>
        <ol className="mt-4 divide-y divide-line rounded-md border border-line">
          {sections.map((s, i) => (
            <li key={s.key} className={`flex items-center gap-3 px-3 py-2.5 ${s.on ? '' : 'bg-surface-raised/50'}`}>
              <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={s.on}
                  disabled={!editable}
                  onChange={(e) => setSections(sections.map((x) => (x.key === s.key ? { ...x, on: e.target.checked } : x)))}
                  className="mt-1 accent-accent"
                />
                <span className="min-w-0">
                  <span className={`block text-sm font-medium ${s.on ? 'text-text-primary' : 'text-text-muted line-through'}`}>{meta(s.key).label}</span>
                  <span className="block text-xs text-text-muted">{meta(s.key).hint}</span>
                </span>
              </label>
              {editable && (
                <span className="flex shrink-0 gap-1">
                  <button type="button" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move ${meta(s.key).label} up`} className="rounded px-2 py-1 text-text-muted hover:bg-surface-raised hover:text-text-primary disabled:opacity-30">↑</button>
                  <button type="button" disabled={i === sections.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${meta(s.key).label} down`} className="rounded px-2 py-1 text-text-muted hover:bg-surface-raised hover:text-text-primary disabled:opacity-30">↓</button>
                </span>
              )}
            </li>
          ))}
        </ol>
        {editable && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button onClick={save} disabled={busy || !dirty} className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50">
              {busy ? 'Saving…' : 'Save'}
            </button>
            {dirty && (
              <button type="button" onClick={() => setSections(JSON.parse(saved))} className="text-sm text-text-muted hover:text-text-primary">
                Undo changes
              </button>
            )}
            {dirty && <span className="text-sm text-warning">Unsaved — the preview below shows your changes.</span>}
            {msg && <span className="text-sm text-success">{msg}</span>}
            {error && <span className="text-sm text-error">{error}</span>}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-text-primary">Preview</h2>
            <p className="mt-0.5 text-sm text-text-muted">Exactly what this client sees — their catalogue, prices, best sellers and amounts owed. Buttons are disabled.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            As
            <select value={previewId} onChange={(e) => setPreviewId(e.target.value)} className="max-w-[18rem] rounded-md border border-line bg-surface px-2 py-1.5 text-sm">
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        {previewId && (
          <div className="mt-4 rounded-lg border border-line bg-background p-4 sm:p-6">
            <ClientHome key={previewId} preview={{ companyId: previewId, layout: sections }} />
          </div>
        )}
      </div>
    </div>
  );
}

// Monthly sales targets in AUD — per store and per rep. The dashboard scales them to the range
// (a quarter is three months) and paces sales against them. Staff can see them; admins set them.
function TargetsSection() {
  const user = useAuthStore((s) => s.user);
  const editable = can(user, CAP.MANAGE_SETTINGS);
  const [t, setT] = useState(null);
  const [team, setTeam] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    fetchTargets().then(setT).catch((e) => setError(e.response?.data?.error?.message || 'Couldn’t load the targets.'));
    fetchTeam().then((l) => setTeam((l || []).filter((u) => u.isActive))).catch(() => setTeam([]));
  }, []);
  if (!t) return <div className="text-sm text-text-muted">{error || 'Loading…'}</div>;
  const num = (v) => (v === '' ? '' : Math.max(0, Number(v) || 0));
  const save = async () => {
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const reps = Object.fromEntries(Object.entries(t.reps).map(([k, v]) => [k, Number(v) || 0]));
      setT(await saveTargets({ monthly: { A: Number(t.monthly.A) || 0, B: Number(t.monthly.B) || 0 }, reps }));
      setMsg('Saved.');
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Couldn’t save.');
    } finally {
      setBusy(false);
    }
  };
  const field = (value, onChange, label) => (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-text-muted">A$</span>
      <input type="number" min="0" step="1000" inputMode="numeric" aria-label={label} disabled={!editable} value={value || ''} placeholder="No target" onChange={(e) => onChange(num(e.target.value))} className="w-36 rounded-md border border-line bg-surface px-2 py-1.5 tabular-nums disabled:bg-surface-raised" />
      <span className="text-text-muted">a month</span>
    </label>
  );
  return (
    <section className="rounded-lg border border-line bg-surface p-5">
      <h2 className="text-lg font-semibold text-text-primary">Sales targets</h2>
      <p className="mt-1 text-sm text-text-muted">Monthly targets in AUD (goods after discounts, before shipping). The home dashboard compares sales with them — a quarter counts as three months, a year as twelve. Leave blank for no target.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div><div className="mb-1 text-sm font-semibold text-text-primary">Australia</div>{field(t.monthly.A, (v) => setT({ ...t, monthly: { ...t.monthly, A: v } }), 'Australia monthly target')}</div>
        <div><div className="mb-1 text-sm font-semibold text-text-primary">International</div>{field(t.monthly.B, (v) => setT({ ...t, monthly: { ...t.monthly, B: v } }), 'International monthly target')}</div>
      </div>
      <h3 className="mt-6 text-sm font-semibold text-text-primary">Per person</h3>
      <p className="text-xs text-text-muted">Counts sales from the clients assigned to them.</p>
      <ul className="mt-2 divide-y divide-line">
        {team.map((u) => (
          <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <span className="text-sm"><span className="font-semibold text-text-primary">{u.name || u.email}</span> <span className="text-xs text-text-muted">· {u.role === 'REP' ? 'Rep' : 'Staff'}</span></span>
            {field(t.reps[u.id], (v) => setT({ ...t, reps: { ...t.reps, [u.id]: v } }), `${u.name || u.email} monthly target`)}
          </li>
        ))}
      </ul>
      {editable ? (
        <div className="mt-4 flex items-center gap-3">
          <button type="button" onClick={save} disabled={busy} className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-60">{busy ? 'Saving…' : 'Save targets'}</button>
          {msg && <span className="text-sm text-success">{msg}</span>}
          {error && <span className="text-sm text-error">{error}</span>}
        </div>
      ) : (
        <p className="mt-4 text-xs text-text-muted">Only admins can change targets.</p>
      )}
    </section>
  );
}

// Focus products: what reps should push. Shown first in every rep's "What to promote" (with the note)
// until the end date, with which of their clients don't stock it. Product ids are per store.
const FOCUS_REASONS = ['Launch', 'Seasonal hero', 'Overstock', 'Best seller', 'Other'];
function FocusSection() {
  const user = useAuthStore((s) => s.user);
  const editable = can(user, CAP.MANAGE_MARKETING);
  const [f, setF] = useState(null);
  const [saved, setSaved] = useState('');
  const [store, setStore] = useState('A');
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const load = (d) => {
    setF(d);
    setSaved(JSON.stringify({ items: d.items, auto: d.auto }));
  };
  useEffect(() => {
    fetchFocus().then(load).catch((e) => setError(e.response?.data?.error?.message || 'Couldn’t load the focus products.'));
  }, []);
  useEffect(() => {
    if (!editable) return undefined;
    let alive = true;
    const t = setTimeout(() => {
      searchFocusProducts(store, q)
        .then((l) => alive && setFound(l))
        .catch(() => alive && setFound([]));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [store, q, editable]);
  if (!f) return <div className="text-sm text-text-muted">{error || 'Loading…'}</div>;
  const dirty = JSON.stringify({ items: f.items, auto: f.auto }) !== saved;
  const update = (i, patch) => setF({ ...f, items: f.items.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const add = (p) => {
    if (f.items.some((x) => x.store === store && x.productId === p.productId)) return;
    const until = new Date(Date.now() + 30 * 864e5).toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
    setF({ ...f, items: [{ store, productId: p.productId, title: p.title, image: p.image, note: '', reason: 'Launch', until, results: null, live: true }, ...f.items] });
  };
  const save = async () => {
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const items = f.items.map(({ store: st, productId, title, note, reason, until, addedAt }) => ({ store: st, productId, title, note, reason, until: until || null, ...(addedAt ? { addedAt } : {}) }));
      load(await saveFocus({ items, auto: f.auto }));
      setMsg('Saved — reps see it on their home page now.');
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Couldn’t save.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-line bg-surface p-5">
        <h2 className="text-lg font-semibold text-text-primary">Focus products</h2>
        <p className="mt-1 text-sm text-text-muted">What you want reps to push. They show first in every rep’s “What to promote”, with your note, until the end date — and each rep sees which of their clients don’t stock it yet. Australia and International are separate catalogues, so add a style once per store to push it in both.</p>

        {editable && (
          <div className="mt-4 rounded-lg border border-line p-3">
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="focus-store">Store</label>
              <select id="focus-store" value={store} onChange={(e) => setStore(e.target.value)} className="rounded-md border border-line bg-surface px-2 py-1.5 text-sm">
                <option value="A">Australia</option>
                <option value="B">International</option>
              </select>
              <label className="sr-only" htmlFor="focus-q">Find a product</label>
              <input id="focus-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a product by name or SKU…" className="min-w-0 flex-1 rounded-md border border-line bg-surface px-3 py-1.5 text-sm" />
            </div>
            <ul className="mt-2 max-h-56 divide-y divide-line overflow-y-auto">
              {found.map((p) => {
                const added = f.items.some((x) => x.store === store && x.productId === p.productId);
                return (
                  <li key={p.productId} className="flex items-center gap-3 py-1.5">
                    {p.image ? <img src={p.image} alt="" className="h-9 w-9 shrink-0 rounded object-cover" /> : <div className="h-9 w-9 shrink-0 rounded bg-surface-raised" />}
                    <span className="min-w-0 flex-1 truncate text-sm text-text-primary">{p.title}</span>
                    <button type="button" disabled={added} onClick={() => add(p)} className="rounded-md border border-line-strong px-2.5 py-1 text-xs font-semibold text-text-primary hover:bg-surface-raised disabled:opacity-50">
                      {added ? 'Added' : 'Add'}
                    </button>
                  </li>
                );
              })}
              {!found.length && <li className="py-2 text-sm text-text-muted">No products match.</li>}
            </ul>
          </div>
        )}

        <ul className="mt-4 space-y-3">
          {f.items.length === 0 && <li className="text-sm text-text-muted">No focus products yet — reps will see new arrivals and online best sellers.</li>}
          {f.items.map((i, idx) => (
            <li key={`${i.store}:${i.productId}`} className="flex flex-wrap items-start gap-4 rounded-lg border border-line p-3">
              {i.image ? <img src={i.image} alt="" className="h-16 w-16 shrink-0 rounded-md object-cover" /> : <div className="h-16 w-16 shrink-0 rounded-md bg-surface-raised" />}
              <div className="min-w-0 flex-[1_1_260px] space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-text-primary">{i.title}</span>
                  <span className="text-xs text-text-muted">{i.store === 'A' ? 'Australia' : 'International'}</span>
                  {i.live === false && <span className="rounded-full bg-surface-raised px-2 py-0.5 text-[11px] font-semibold text-text-muted">Ended</span>}
                  {i.inCatalogue === false && <span className="rounded-full bg-error-light px-2 py-0.5 text-[11px] font-semibold text-error">No longer in the catalogue</span>}
                </div>
                <label className="block text-xs font-medium text-text-secondary">
                  Note to reps
                  <textarea rows={2} maxLength={400} disabled={!editable} value={i.note} onChange={(e) => update(idx, { note: e.target.value })} placeholder="Why push it — e.g. “Homepage hero from 15 Nov, get it in stores first”" className="mt-1 block w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm font-normal text-text-primary disabled:bg-surface-raised" />
                </label>
              </div>
              <div className="flex flex-[0_1_200px] flex-col gap-2">
                <label className="text-xs font-medium text-text-secondary">
                  Why
                  <select disabled={!editable} value={i.reason} onChange={(e) => update(idx, { reason: e.target.value })} className="mt-1 block w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-text-primary disabled:bg-surface-raised">
                    {FOCUS_REASONS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </label>
                <label className="text-xs font-medium text-text-secondary">
                  Show until
                  <input type="date" disabled={!editable} value={i.until || ''} onChange={(e) => update(idx, { until: e.target.value })} className="mt-1 block w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-text-primary disabled:bg-surface-raised" />
                </label>
              </div>
              <div className="flex flex-[0_1_180px] flex-col gap-1 text-xs text-text-secondary">
                <span className="font-semibold text-text-primary">Since it went live</span>
                {i.results ? (
                  <>
                    <span>{i.results.clients} client{i.results.clients === 1 ? '' : 's'} ordered it</span>
                    <span>{i.results.units} units · {fmtMoney(i.results.value, 'AUD')}</span>
                  </>
                ) : (
                  <span>Starts counting when you save</span>
                )}
                {editable && (
                  <button type="button" onClick={() => setF({ ...f, items: f.items.filter((_, j) => j !== idx) })} className="mt-1 self-start text-xs font-semibold text-error hover:underline">
                    Remove
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-lg border border-line bg-surface p-5">
        <h2 className="text-base font-semibold text-text-primary">Also shown to reps automatically</h2>
        <label className="mt-3 flex items-center gap-2 text-sm text-text-primary">
          <input type="checkbox" disabled={!editable} checked={f.auto.newArrivals} onChange={(e) => setF({ ...f, auto: { ...f.auto, newArrivals: e.target.checked } })} className="h-4 w-4" />
          New arrivals — products added to the catalogue in the last 30 days
        </label>
        <label className="mt-2 flex items-center gap-2 text-sm text-text-primary">
          <input type="checkbox" disabled={!editable} checked={f.auto.online} onChange={(e) => setF({ ...f, auto: { ...f.auto, online: e.target.checked } })} className="h-4 w-4" />
          Selling online — the top sellers on your websites
        </label>
      </section>

      {editable ? (
        <div className="flex items-center gap-3">
          <button type="button" onClick={save} disabled={busy || !dirty} className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-60">{busy ? 'Saving…' : 'Save focus products'}</button>
          {msg && <span className="text-sm text-success">{msg}</span>}
          {error && <span className="text-sm text-error">{error}</span>}
        </div>
      ) : (
        <p className="text-xs text-text-muted">Only marketing managers can change focus products.</p>
      )}
    </div>
  );
}

const TABS = [
  { key: 'team', label: 'Team & access', render: () => <TeamSection /> },
  { key: 'emails', label: 'Emails', render: () => (<><InvoiceEmailSection /><ClientInviteSection /><CustomerMessagesSection /></>) },
  { key: 'ordering', label: 'Ordering', render: () => (<><ApprovalSection /><MinOrderSection /></>) },
  { key: 'client-home', label: 'Client home', render: () => <ClientHomeSection /> },
  { key: 'targets', label: 'Targets', render: () => <TargetsSection /> },
  { key: 'focus', label: 'Focus products', render: () => <FocusSection /> },
  { key: 'billing', label: 'Billing', cap: CAP.MANAGE_SETTINGS, render: () => <RemittanceSection /> },
  { key: 'data', label: 'Data & sync', render: () => <SyncSection /> },
];

export default function Settings() {
  const user = useAuthStore((s) => s.user);
  const tabs = TABS.filter((t) => !t.cap || can(user, t.cap));
  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => params.get('tab') || 'team');
  const active = tabs.find((t) => t.key === tab) || tabs[0];
  return (
    <>
      <PageHeader title="Settings" subtitle="Manage your team, client emails, ordering rules, and data." />
      <div className="mb-4 flex flex-wrap gap-1 border-b border-line">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition ${
              tab === t.key ? 'border-accent text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {active.render()}
    </>
  );
}
